import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Box,
  TextField,
  Checkbox,
  IconButton,
  Table,
  TableBody,
  TableRow,
  TableCell,
  SelectChangeEvent,
  Tooltip,
  useTheme,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { LoginUserInfo } from "IF/interfaceType";
import { useThemeMode } from "Contexts/ThemeContext";

interface MobileUserProps {
  row: LoginUserInfo;
  index: number;
  rentalFlg: boolean;
  editFormData: LoginUserInfo;
  onEdit: (index: number) => void;
  handleChange: (
    event:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<string>,
    child?: React.ReactNode
  ) => void;
  handleCheckboxChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleUpdate: () => void;
  handleCancel: () => void;
  handleDelete?: (index: number) => void;
  openModal: (message: string, confirmAction: () => void) => void;
  width: number;
}

const SettingUserMobile: React.FC<MobileUserProps> = ({
  row,
  index,
  rentalFlg,
  editFormData,
  onEdit,
  handleChange,
  handleCheckboxChange,
  handleUpdate,
  handleCancel,
  handleDelete,
  openModal,
  width,
}) => {
  const handleOpenModal = (id: number) => {
    openModal("選択したユーザー情報を削除します。よろしいですか？", () =>
      handleDelete?.(id)
    );
  };

  const theme = useTheme();
  const { mode } = useThemeMode();

  const rowHeight = "30px";

  return (
    <Card
      sx={{
        flexGrow: 1,
        backgroundColor: theme.palette.custom.sForm,
        gap: 1,
        maxWidth: "100%",
        p: 0,
      }}
    >
      <CardContent>
        <Table>
          <TableBody>
            {/* 管理者 */}
            {!rentalFlg && (
              <>
                <TableRow
                  sx={{
                    "& td, & th": {
                      borderBottom: "none",
                    },
                    height: rowHeight,
                  }}
                >
                  <TableCell
                    scope="row"
                    align="center"
                    size="small"
                    sx={{
                      fontSize: "0.9rem",
                      fontWeight: "fontWeightRegular",
                      width: "40%",
                      p: 0.5,
                      height: rowHeight,
                    }}
                  >
                    管理者：
                  </TableCell>
                  <TableCell
                    align="left"
                    size="small"
                    sx={{
                      fontSize: "0.8rem",
                      fontWeight: "fontWeightRegular",
                      width: "60%",
                      p: 0.5,
                      height: rowHeight,
                    }}
                  >
                    {editFormData.id === row.id ? (
                      <Checkbox
                        checked={editFormData.adminFlg}
                        onChange={handleCheckboxChange}
                        aria-label={`管理者チェックボックス-${index}`}
                      />
                    ) : (
                      <Checkbox
                        checked={row.adminFlg}
                        disabled
                        aria-label={`管理者チェックボックス-${index}`}
                      />
                    )}
                  </TableCell>
                </TableRow>
              </>
            )}
            {/* ユーザーID*/}
            <TableRow
              sx={{
                "& td, & th": {
                  borderBottom: "none",
                },
              }}
            >
              <TableCell
                scope="row"
                align="center"
                size="small"
                sx={{
                  fontSize: "0.9rem",
                  fontWeight: "fontWeightRegular",
                  p: 0.5,
                  height: rowHeight,
                  width: "40%",
                }}
              >
                ログインID：
              </TableCell>
              <TableCell
                scope="row"
                align="center"
                size="small"
                sx={{
                  fontSize: "0.8rem",
                  fontWeight: "fontWeightRegular",
                  p: 0.5,
                  height: rowHeight,
                  width: "60%",
                }}
              >
                {editFormData.id === row.id ? (
                  <TextField
                    size={"small"}
                    value={editFormData?.userId}
                    fullWidth
                    name="userId"
                    onChange={handleChange}
                    InputProps={{
                      sx: {
                        height: 30,
                        fontSize: "0.8rem",
                        padding: "0 8px",
                      },
                    }}
                  />
                ) : (
                  <TextField
                    size={"small"}
                    value={row.userId}
                    fullWidth
                    name="userId"
                    disabled
                    InputProps={{
                      sx: {
                        height: 30,
                        fontSize: "0.8rem",
                        padding: "0 8px",
                        "& .MuiInputBase-input.Mui-disabled": {
                          WebkitTextFillColor:
                            mode === "dark" ? "white" : "black",
                        },
                      },
                    }}
                  />
                )}
              </TableCell>
            </TableRow>
            {/* ユーザーパスワード*/}
            <TableRow
              sx={{
                "& td, & th": {
                  borderBottom: "none",
                },
              }}
            >
              <TableCell
                scope="row"
                align="center"
                size="small"
                sx={{
                  fontSize: "0.9rem",
                  fontWeight: "fontWeightRegular",
                  p: 0.5,
                  height: rowHeight,
                  width: "40%",
                }}
              >
                パスワード：
              </TableCell>
              <TableCell
                scope="row"
                align="center"
                size="small"
                sx={{
                  fontSize: "0.8rem",
                  fontWeight: "fontWeightRegular",
                  p: 0.5,
                  height: rowHeight,
                  width: "60%",
                }}
              >
                {editFormData.id === row.id ? (
                  <TextField
                    size={"small"}
                    value={editFormData?.userPassword}
                    name="userPassword"
                    fullWidth
                    onChange={handleChange}
                    InputProps={{
                      sx: {
                        height: 30,
                        fontSize: "0.8rem",
                        padding: "0 8px",
                      },
                    }}
                  />
                ) : (
                  <TextField
                    size={"small"}
                    value={row.userPassword}
                    name="userPassword"
                    fullWidth
                    disabled
                    InputProps={{
                      sx: {
                        height: 30,
                        fontSize: "0.8rem",
                        padding: "0 8px",
                        "& .MuiInputBase-input.Mui-disabled": {
                          WebkitTextFillColor:
                            mode === "dark" ? "white" : "black",
                        },
                      },
                    }}
                  />
                )}
              </TableCell>
            </TableRow>
            {/* ユーザー名*/}
            <TableRow
              sx={{
                "& td, & th": {
                  borderBottom: "none",
                },
              }}
            >
              <TableCell
                scope="row"
                align="center"
                size="small"
                sx={{
                  fontSize: "0.9rem",
                  fontWeight: "fontWeightRegular",
                  p: 0.5,
                  height: rowHeight,
                  width: "40%",
                }}
              >
                ユーザー名：
              </TableCell>
              <TableCell
                scope="row"
                align="center"
                size="small"
                sx={{
                  fontSize: "0.8rem",
                  fontWeight: "fontWeightRegular",
                  p: 0.5,
                  height: rowHeight,
                  width: "60%",
                }}
              >
                {editFormData.id === row.id ? (
                  <TextField
                    size={"small"}
                    value={editFormData?.userName}
                    name="userName"
                    fullWidth
                    onChange={handleChange}
                    InputProps={{
                      sx: {
                        height: 30,
                        fontSize: "0.8rem",
                        padding: "0 8px",
                      },
                    }}
                  />
                ) : (
                  <TextField
                    size={"small"}
                    value={row.userName}
                    name="userName"
                    fullWidth
                    disabled
                    InputProps={{
                      sx: {
                        height: 30,
                        fontSize: "0.8rem",
                        padding: "0 8px",
                        "& .MuiInputBase-input.Mui-disabled": {
                          WebkitTextFillColor:
                            mode === "dark" ? "white" : "black",
                        },
                      },
                    }}
                  />
                )}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <Box display="flex" justifyContent="flex-end" sx={{ pb: 1 }}>
          {editFormData.id === row.id ? (
            <>
              <Tooltip title={"保存"} placement="top" arrow>
                <IconButton onClick={handleUpdate}>
                  <SaveIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={"キャンセル"} placement="top" arrow>
                <IconButton onClick={handleCancel}>
                  <CancelIcon />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <>
              <Tooltip title={"編集"} placement="top" arrow>
                <IconButton onClick={() => onEdit(index)}>
                  <EditIcon />
                </IconButton>
              </Tooltip>
              {!rentalFlg && (
                <Tooltip title={"削除"} placement="top" arrow>
                  <IconButton onClick={() => handleOpenModal(index)}>
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default SettingUserMobile;
